import React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { JellyTriangle } from 'ldrs/react';
import 'ldrs/react/JellyTriangle.css';
import { getIdeas } from '../services/api';
import IdeaList from '../components/IdeaList';
// Modal view replaced by dedicated IdeaPage route
import FilterPanel from '../components/FilterPanel';
import { stringToColor, readableTextColor } from '../utils/color';
import { Helmet } from 'react-helmet';

function IndexPage() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMasters, setSelectedMasters] = useState([]);
  const [selectedCats, setSelectedCats] = useState([]);
  const [query, setQuery] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const data = await getIdeas();
        if (mounted) {
          const sorted = (data || []).slice().sort((a, b) => (b?.score || 0) - (a?.score || 0));
          setIdeas(sorted);
        }
      } catch (e) {
        if (mounted) setError(e);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const visibleIdeas = useMemo(() => {
    const tokens = (query || '').toLowerCase().trim().split(/\s+/).filter(Boolean);
    return ideas.filter((i) => {
      const m = i?.master_category || null;
      const c = i?.category?.name || null;
      const okMaster = (selectedMasters && selectedMasters.length > 0) ? (m ? selectedMasters.includes(m) : false) : true;
      const okCat = (selectedCats && selectedCats.length > 0) ? (c ? selectedCats.includes(c) : false) : true;
      if (!(okMaster && okCat)) return false;
      if (tokens.length === 0) return true;
      const hay = `${i?.title || ''} ${i?.description || ''}`.toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  }, [ideas, selectedMasters, selectedCats, query]);

  function toggleCategory(name) {
    setSelectedCats((prev) => {
      const set = new Set(prev);
      if (set.has(name)) set.delete(name); else set.add(name);
      return Array.from(set);
    });
  }

  function toggleMaster(name) {
    setSelectedMasters((prev) => {
      const set = new Set(prev);
      if (set.has(name)) set.delete(name); else set.add(name);
      return Array.from(set);
    });
    // Reset sub-categories when master set changes to avoid stale selection out of scope
    setSelectedCats([]);
  }

  function addMasterAndCategory(masterName, categoryName) {
    if (masterName) {
      setSelectedMasters((prev) => (prev.includes(masterName) ? prev : [...prev, masterName]));
    }
    if (categoryName) {
      setSelectedCats((prev) => (prev.includes(categoryName) ? prev : [...prev, categoryName]));
    }
  }

  return (
    <main className="container" style={{ paddingTop: 16, paddingBottom: 32 }}>
      <Helmet>
        <title>The Big Idea — Discover high-impact product ideas</title>
        <meta name="description" content="Discover what people truly need — explore, filter, and prioritize high-impact product ideas with a simple, frictionless interface." />
        <link rel="canonical" href={(process.env.REACT_APP_SITE_URL || 'https://bigidea.muware.xyz') + '/'} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="The Big Idea" />
        <meta property="og:title" content="The Big Idea — Discover high-impact product ideas" />
        <meta property="og:description" content="Explore, filter, and prioritize high-impact product ideas." />
        <meta property="og:url" content={(process.env.REACT_APP_SITE_URL || 'https://bigidea.muware.xyz') + '/'} />
        <meta property="og:image" content={(process.env.REACT_APP_SITE_URL || 'https://bigidea.muware.xyz') + '/logo512.png'} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Big Idea — Discover high-impact product ideas" />
        <meta name="twitter:description" content="Explore, filter, and prioritize high-impact product ideas." />
        <meta name="twitter:image" content={(process.env.REACT_APP_SITE_URL || 'https://bigidea.muware.xyz') + '/logo512.png'} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebApplication',
          name: 'The Big Idea',
          url: (process.env.REACT_APP_SITE_URL || 'https://bigidea.muware.xyz') + '/',
          description: 'Discover what people truly need — explore, filter, and prioritize high-impact product ideas.',
          applicationCategory: 'BusinessApplication',
          operatingSystem: 'Web',
        })}</script>
      </Helmet>

      {/* Always show hero and search */}
      <section className="hero" style={{ marginBottom: 8 }}>
        <h1 className="hero-title">The Big Idea</h1>
        <p className="hero-subtitle">Discover what people truly need — explore, filter, and prioritize high-impact ideas with a simple, frictionless interface.</p>
      </section>
      <section className="panel panel--muted" style={{ marginBottom: 16 }}>
        <div className="form-row">
          <div>
            <input
              className="field field--lg"
              placeholder="Search by title or description..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
        </div>
        {(selectedMasters.length > 0 || selectedCats.length > 0) && (
          <div className="grid" style={{ marginTop: 12, gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {selectedMasters.map((m) => {
                const bg = stringToColor(m); const color = readableTextColor(bg);
                return (
                  <span key={`sel-m-${m}`} className="chip" style={{ background: bg, color }}>
                    {m}
                    <button className="chip-close" onClick={() => toggleMaster(m)} aria-label={`Remove ${m}`}>
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </span>
                );
              })}
              {selectedCats.map((c) => {
                const bg = stringToColor(c); const color = readableTextColor(bg);
                return (
                  <span key={`sel-c-${c}`} className="chip" style={{ background: bg, color }}>
                    {c}
                    <button className="chip-close" onClick={() => toggleCategory(c)} aria-label={`Remove ${c}`}>
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}
      </section>

      {/* Content area: loader or grid+filters */}
      {error && (
        <section className="panel" style={{ borderColor: 'var(--color-danger)' }}>
          <p>Failed to load ideas (status {error.status || 'n/a'}).</p>
        </section>
      )}

      {loading ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: '48px 0' }}>
          <JellyTriangle size="40" speed="1.6" color="black" />
        </div>
      ) : (
        <div className="layout-main">
          <FilterPanel ideas={ideas} selectedMasters={selectedMasters} selectedCategories={selectedCats} onToggleMaster={toggleMaster} onToggleCategory={toggleCategory} />
          <section className="panel panel--muted">
            <IdeaList ideas={visibleIdeas} onOpen={() => {}} />
          </section>
        </div>
      )}

      {/* Mobile FAB to open filters */}
      <button className="fab" onClick={() => setDrawerOpen(true)} aria-label="Open filters">
        <i className="fa-solid fa-sliders" />
      </button>

      {/* Drawer for filters on mobile */}
      <div className={`drawer-overlay ${drawerOpen ? 'open' : ''}`} onClick={() => setDrawerOpen(false)} />
      <div className={`drawer ${drawerOpen ? 'open' : ''}`} role="dialog" aria-modal="true" aria-label="Filters">
        <div className="drawer-header">
          <div />
          <button className="drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Close filters">
            <i className="fa-solid fa-xmark" />
          </button>
        </div>
        <div className="drawer-body">
          <FilterPanel ideas={ideas} selectedMasters={selectedMasters} selectedCategories={selectedCats} onToggleMaster={(m) => { toggleMaster(m); }} onToggleCategory={(c) => { toggleCategory(c); }} />
        </div>
      </div>
      {/* Modal removed; navigation handled by IdeaCard to /idea/:id */}
    </main>
  );
}

export default IndexPage;


